import {
  Box,
  Button,
  Card,
  Container,
  Group,
  List,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconArrowRight,
  IconBuildingHospital,
  IconChefHat,
  IconArmchair,
  IconMedicineSyrup,
  IconScissors,
  IconMassage,
  IconPlant,
  IconFlame,
  IconStretching,
  IconSun,
  IconToolsKitchen2,
  IconTrees,
  IconUsers,
  IconWash,
} from '@tabler/icons-react';
import { Link } from 'react-router';

const amenities = [
  {
    icon: IconToolsKitchen2,
    title: 'Commercial Kitchen',
    description: 'Our full-scale commercial kitchen allows us to prepare fresh, nutritious meals daily. Professional-grade equipment ensures proper food safety and the ability to accommodate diverse dietary needs.',
  },
  {
    icon: IconArmchair,
    title: 'Large Community Areas',
    description: 'Spacious community rooms on each floor provide comfortable gathering spaces for activities, socializing, and family visits. Designed for accessibility and engagement.',
  },
  {
    icon: IconChefHat,
    title: 'Dining Room Per Floor',
    description: 'Each floor features its own dedicated dining area, making mealtimes convenient and fostering a sense of community among residents on each level.',
  },
  {
    icon: IconScissors,
    title: 'On-Site Beauty Shop',
    description: 'Our built-in beauty salon offers residents convenient access to haircuts, styling, and grooming services without leaving the building.',
  },
  {
    icon: IconMedicineSyrup,
    title: 'Secure Medicine Storage',
    description: 'Professional-grade medication storage facilities with proper climate control and security, ensuring safe and organized medication management for all residents.',
  },
  {
    icon: IconWash,
    title: 'Commercial Laundry',
    description: 'Industrial laundry facilities enable us to provide prompt, thorough cleaning services for all resident linens and personal items.',
  },
  {
    icon: IconSun,
    title: 'Large Outdoor Area',
    description: 'Expansive outdoor spaces with accessible pathways, seating areas, and gardens where residents can enjoy fresh air, sunshine, and nature.',
  },
  {
    icon: IconTrees,
    title: 'Adjacent to Park',
    description: 'Our location next to a beautiful park provides scenic views and easy access to additional green space for walks and outdoor activities.',
  },
  {
    icon: IconMassage,
    title: 'Massage Chairs',
    description: 'Therapeutic massage chairs available for residents to relax, relieve tension, and promote circulation and overall well-being.',
  },
  {
    icon: IconFlame,
    title: 'Sauna',
    description: 'On-site sauna facilities for relaxation and therapeutic benefit, supporting joint comfort, stress relief, and improved sleep.',
  },
  {
    icon: IconPlant,
    title: 'Alternative Medicine',
    description: 'Access to holistic and complementary wellness options including herbal remedies, acupuncture referrals, and mindfulness programs.',
  },
  {
    icon: IconStretching,
    title: 'OT/PT Services',
    description: 'On-site occupational and physical therapy services to help residents maintain strength, mobility, and independence in daily life.',
  },
];

const benefits = [
  'Wide hallways designed for wheelchair and mobility device accessibility',
  'Emergency response systems throughout the building',
  'Nurse call systems in every room',
  'Backup generator for uninterrupted power',
  'Fire suppression and safety systems exceeding residential standards',
  'ADA-compliant bathrooms and common areas',
  'Professional ventilation and air quality systems',
  'Elevator access to all floors',
];

export function FacilityPage() {
  return (
    <>
      {/* Hero Section */}
      <Box py={80} bg="gray.0">
        <Container size="xl">
          <Stack align="center" gap="md" maw={800} mx="auto">
            <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
              Our Facility
            </Text>
            <Title ta="center">
              Built for Professional Care, Designed for Comfort, Updated for Independence
            </Title>
            <Text c="dimmed" ta="center" size="lg">
              Our building was originally constructed as a skilled nursing facility, giving us access
              to professional-grade amenities and infrastructure that most assisted living communities
              simply cannot offer.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Introduction Section */}
      <Box py={80}>
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={50}>
            <Stack justify="center" gap="lg">
              <ThemeIcon size={60} radius="xl" variant="light" color="harmony">
                <IconBuildingHospital size={30} />
              </ThemeIcon>
              <Title order={2}>
                Skilled Nursing Infrastructure, Assisted Living Care
              </Title>
              <Text c="dimmed" size="lg">
                When you choose Hillside Harmony, you're choosing a facility that was built to the
                highest standards of healthcare construction. Our building was designed and constructed
                as a skilled nursing facility, which means every aspect of the infrastructure exceeds
                typical assisted living requirements.
              </Text>
              <Text c="dimmed">
                This unique heritage allows us to provide assisted living services in an environment
                with professional-grade amenities, superior safety features, and spacious common areas
                that most assisted living facilities simply cannot match.
              </Text>
            </Stack>
            <Card padding="xl" bg="harmony.0" radius="lg">
              <Stack gap="md">
                <Title order={3} c="harmony.7">
                  What This Means for You
                </Title>
                <List
                  spacing="sm"
                  icon={
                    <ThemeIcon size={24} radius="xl" color="harmony">
                      <IconArrowRight size={14} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>
                    <Text fw={500}>Superior Safety Standards</Text>
                    <Text size="sm" c="dimmed">Built to meet strict healthcare facility codes</Text>
                  </List.Item>
                  <List.Item>
                    <Text fw={500}>Professional-Grade Amenities</Text>
                    <Text size="sm" c="dimmed">Commercial kitchen, laundry, and medical storage</Text>
                  </List.Item>
                  <List.Item>
                    <Text fw={500}>Spacious Common Areas</Text>
                    <Text size="sm" c="dimmed">Large gathering spaces on every floor</Text>
                  </List.Item>
                  <List.Item>
                    <Text fw={500}>Accessible Design</Text>
                    <Text size="sm" c="dimmed">Wide hallways, elevators, and ADA compliance throughout</Text>
                  </List.Item>
                </List>
              </Stack>
            </Card>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Amenities Section */}
      <Box py={80} bg="gray.0">
        <Container size="xl">
          <Stack align="center" gap="sm" mb={50}>
            <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
              Facility Amenities
            </Text>
            <Title order={2} ta="center">
              Professional-Grade Features
            </Title>
            <Text c="dimmed" ta="center" maw={600}>
              Take advantage of amenities typically found only in skilled nursing facilities,
              all while enjoying the warmth and comfort of assisted living care.
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="xl">
            {amenities.map((amenity) => (
              <Card key={amenity.title} padding="lg">
                <ThemeIcon
                  size={50}
                  radius="md"
                  variant="light"
                  color="harmony"
                  mb="md"
                >
                  <amenity.icon size={26} stroke={1.5} />
                </ThemeIcon>
                <Text fw={600} size="lg" mb="xs">
                  {amenity.title}
                </Text>
                <Text c="dimmed" size="sm">
                  {amenity.description}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Infrastructure Benefits Section */}
      <Box py={80}>
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={50}>
            <Stack justify="center" gap="lg">
              <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
                Built-In Benefits
              </Text>
              <Title order={2}>
                Healthcare-Grade Infrastructure
              </Title>
              <Text c="dimmed">
                Our skilled nursing facility construction provides built-in advantages that
                enhance safety, accessibility, and quality of life for our residents.
              </Text>
            </Stack>
            <Card padding="xl" radius="lg">
              <SimpleGrid cols={1} spacing="sm">
                {benefits.map((benefit, index) => (
                  <Group key={index} gap="sm" wrap="nowrap">
                    <ThemeIcon size={24} radius="xl" color="harmony" variant="light">
                      <IconArrowRight size={14} />
                    </ThemeIcon>
                    <Text size="sm">{benefit}</Text>
                  </Group>
                ))}
              </SimpleGrid>
            </Card>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Location Section */}
      <Box py={80} bg="harmony.7">
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={50}>
            <Stack justify="center" gap="lg">
              <ThemeIcon size={60} radius="xl" color="white" c="harmony.7">
                <IconTrees size={30} />
              </ThemeIcon>
              <Title order={2} c="white">
                A Beautiful Location
              </Title>
              <Text c="white" opacity={0.9} size="lg">
                Situated next to a beautiful park, our facility offers residents easy access to
                nature and outdoor activities. The large outdoor area provides space for gardening,
                walking paths, and comfortable seating areas where residents can enjoy the fresh air
                and scenic surroundings.
              </Text>
              <Group gap="md">
                <Button
                  component={Link}
                  to="/contact"
                  size="lg"
                  color="white"
                  c="harmony.7"
                  rightSection={<IconArrowRight size={18} />}
                >
                  Schedule a Tour
                </Button>
              </Group>
            </Stack>
            <Stack justify="center" gap="lg">
              <Card padding="xl" radius="lg">
                <Stack gap="md">
                  <Group gap="sm">
                    <ThemeIcon size={40} radius="md" variant="light" color="harmony">
                      <IconUsers size={20} />
                    </ThemeIcon>
                    <Box>
                      <Text fw={600}>Community Spaces</Text>
                      <Text size="sm" c="dimmed">Multiple gathering areas for activities and socializing</Text>
                    </Box>
                  </Group>
                  <Group gap="sm">
                    <ThemeIcon size={40} radius="md" variant="light" color="harmony">
                      <IconSun size={20} />
                    </ThemeIcon>
                    <Box>
                      <Text fw={600}>Outdoor Access</Text>
                      <Text size="sm" c="dimmed">Safe, accessible outdoor areas with seating and shade</Text>
                    </Box>
                  </Group>
                  <Group gap="sm">
                    <ThemeIcon size={40} radius="md" variant="light" color="harmony">
                      <IconTrees size={20} />
                    </ThemeIcon>
                    <Box>
                      <Text fw={600}>Park Adjacent</Text>
                      <Text size="sm" c="dimmed">Beautiful park views and easy access to green space</Text>
                    </Box>
                  </Group>
                </Stack>
              </Card>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={80}>
        <Container size="md">
          <Card padding="xl" radius="lg" bg="harmony.0">
            <Stack align="center" gap="lg">
              <Title order={2} ta="center">
                See Our Facility in Person
              </Title>
              <Text c="dimmed" ta="center" maw={500}>
                The best way to appreciate our professional-grade amenities is to experience them
                firsthand. Schedule a tour and see why our facility stands apart.
              </Text>
              <Group gap="md">
                <Button
                  component={Link}
                  to="/contact"
                  size="lg"
                  rightSection={<IconArrowRight size={18} />}
                >
                  Schedule a Tour
                </Button>
                <Button
                  component="a"
                  href="tel:+16514000063"
                  size="lg"
                  variant="outline"
                >
                  Call (651) 400-0063
                </Button>
              </Group>
            </Stack>
          </Card>
        </Container>
      </Box>
    </>
  );
}
